import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled
} from "@ant-design/icons"
import {
  Alert,
  App,
  Button,
  Descriptions,
  Drawer,
  Skeleton,
  Tag,
  Typography
} from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { TextArea } from "../../../components/Input/TextArea.tsx"
import { RichTextViewer } from "../../../components/RichTextEditor/RichTextViewer.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { answerReply, getMessage } from "../api/messages.ts"
import { useCommunicationRights } from "../communication-permissions.ts"
import type { MessageDetail, Reply } from "../types/messages.types.ts"
import { MessageStatusTag } from "./MessageStatusTag.tsx"

const FAILED = new Set(["failed", "bounced", "complained"])

interface Step {
  key: string
  at: string | null
  failed?: boolean
}

export const lifecycle = (message: MessageDetail): Step[] => {
  const steps: Step[] = [{ key: "sent", at: message.sent_at }]
  if (FAILED.has(message.status)) {
    steps.push({ key: message.status, at: message.failed_at, failed: true })
    return steps
  }
  if (message.status === "skipped")
    return [{ key: "skipped", at: message.skipped_at, failed: true }]
  steps.push({ key: "delivered", at: message.delivered_at })
  steps.push({ key: "replied", at: message.first_replied_at })
  return steps
}

const recordLink = (record: MessageDetail["record"]) =>
  !record
    ? null
    : record.type === "project"
      ? RoutesPaths.project.view(record.id).overview
      : RoutesPaths.lead.view(record.id)

const MessageDetailDrawer = ({
  messageId,
  onClose
}: {
  messageId: string | null
  onClose: () => void
}) => {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()
  const { message: toast } = App.useApp()
  const rights = useCommunicationRights()
  const [message, setMessage] = useState<MessageDetail | null>(null)
  const [failed, setFailed] = useState(false)
  const [answering, setAnswering] = useState<string | null>(null)
  const [answer, setAnswer] = useState("")
  const [sending, setSending] = useState(false)

  const load = useCallback(
    (id: string, isCurrent: () => boolean = () => true) =>
      getMessage(id).then((response) => {
        if (!isCurrent()) return
        if (response.ok && response.data) setMessage(response.data)
        else setFailed(true)
      }),
    []
  )

  useEffect(() => {
    if (!messageId) return
    setMessage(null)
    setFailed(false)
    setAnswering(null)
    let current = true
    void load(messageId, () => current)
    return () => {
      current = false
    }
  }, [messageId, load])

  const startAnswer = (reply: Reply) => {
    setAnswer("")
    setAnswering(reply.id)
  }

  const sendAnswer = async (reply: Reply) => {
    setSending(true)
    const response = await answerReply(reply.id, answer.trim())
    setSending(false)
    if (!response.ok) {
      const detail = (
        response.data as { details?: { detail?: unknown } } | undefined
      )?.details?.detail
      toast.error(
        typeof detail === "string"
          ? detail
          : t("communication.messages.detail.answerFailed")
      )
      return
    }
    setAnswering(null)
    toast.success(t("communication.messages.detail.answerSent"))
    if (messageId) void load(messageId)
  }

  const link = message ? recordLink(message.record) : null

  return (
    <Drawer
      open={!!messageId}
      onClose={onClose}
      width={640}
      destroyOnHidden
      title={message?.subject || t("communication.messages.detail.title")}
      extra={
        link && (
          <Link to={link}>
            <Button>
              {message?.record?.type === "project"
                ? t("communication.messages.detail.openProject")
                : t("communication.messages.detail.openLead")}
            </Button>
          </Link>
        )
      }
    >
      {failed ? (
        <Alert
          type="error"
          showIcon
          message={t("communication.messages.detail.loadFailed")}
        />
      ) : !message ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <div className="flex flex-col gap-4">
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t("communication.messages.detail.from")}>
              {[message.from_name, message.from_address]
                .filter(Boolean)
                .join(" · ") || "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("communication.messages.detail.to")}>
              {message.to_address || "—"}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("communication.messages.detail.sentBy")}
            >
              {message.sender_name ?? t("communication.messages.automated")}
            </Descriptions.Item>
            <Descriptions.Item label={t("communication.messages.detail.when")}>
              {formatDate(message.created)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("communication.messages.columns.status")}
            >
              <MessageStatusTag status={message.status} />
            </Descriptions.Item>
          </Descriptions>

          <ol
            aria-label={t("communication.messages.detail.lifecycle")}
            className="flex flex-wrap gap-4"
          >
            {lifecycle(message).map((step) => (
              <li key={step.key} className="flex items-center gap-2">
                {step.failed ? (
                  <CloseCircleFilled className="text-red-600" aria-hidden />
                ) : step.at ? (
                  <CheckCircleFilled className="text-primary" aria-hidden />
                ) : (
                  <ClockCircleOutlined className="text-gray-400" aria-hidden />
                )}
                <span>{t(`communication.messages.status.${step.key}`)}</span>
                {step.at && (
                  <Typography.Text type="secondary">
                    {formatDate(step.at)}
                  </Typography.Text>
                )}
              </li>
            ))}
          </ol>

          {message.failure_reason && (
            <Alert type="error" showIcon message={message.failure_reason} />
          )}

          <section
            aria-label={t("communication.messages.detail.body")}
            className="rounded-lg border border-solid border-gray-200 p-4"
          >
            {message.channel === "email" && message.body_html ? (
              <RichTextViewer html={message.body_html} allowImages />
            ) : (
              <p className="whitespace-pre-wrap">{message.body_text}</p>
            )}
          </section>

          <section aria-label={t("communication.messages.detail.replies")}>
            <Typography.Title level={5}>
              {t("communication.messages.detail.repliesTitle", {
                count: message.replies.length
              })}
            </Typography.Title>
            {message.replies.length === 0 ? (
              <Typography.Text type="secondary">
                {t("communication.messages.detail.noReplies")}
              </Typography.Text>
            ) : (
              <ul className="flex flex-col gap-3">
                {message.replies.map((reply) => (
                  <li key={reply.id} className="rounded-lg bg-gray-50 p-3">
                    <p className="mb-1">
                      <Typography.Text strong>
                        {reply.from_name || reply.from_address}
                      </Typography.Text>{" "}
                      <Tag>
                        {t(`communication.templates.channel.${reply.channel}`)}
                      </Tag>
                      <Typography.Text type="secondary">
                        {formatDate(reply.received_at)}
                      </Typography.Text>
                    </p>
                    <p className="whitespace-pre-wrap">{reply.body_text}</p>
                    {(reply.answers ?? []).length > 0 && (
                      <ul
                        aria-label={t("communication.messages.detail.answers")}
                        className="mt-2 flex flex-col gap-2 border-l-2 border-gray-200 pl-3"
                      >
                        {(reply.answers ?? []).map((sent) => (
                          <li key={sent.id}>
                            <Typography.Text
                              type="secondary"
                              className="text-xs"
                            >
                              {t("communication.messages.detail.answeredBy", {
                                name:
                                  sent.sender_name ||
                                  t(
                                    "communication.messages.detail.yourCompany"
                                  ),
                                date: formatDate(sent.sent_at ?? sent.created)
                              })}
                            </Typography.Text>
                            <p className="whitespace-pre-wrap">
                              {sent.body_text}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                    {rights.send && answering !== reply.id && (
                      <Button
                        size="small"
                        className="mt-2"
                        onClick={() => startAnswer(reply)}
                      >
                        {t("communication.messages.detail.reply")}
                      </Button>
                    )}
                    {answering === reply.id && (
                      <div className="mt-2 flex flex-col gap-2">
                        <TextArea
                          autoSize={{ minRows: 3 }}
                          maxLength={reply.channel === "sms" ? 1600 : 10000}
                          aria-label={t(
                            "communication.messages.detail.answerLabel",
                            {
                              name: reply.from_name || reply.from_address
                            }
                          )}
                          value={answer}
                          onChange={(event) => setAnswer(event.target.value)}
                        />
                        <span className="flex gap-2">
                          <Button
                            type="primary"
                            size="small"
                            loading={sending}
                            disabled={!answer.trim()}
                            onClick={() => void sendAnswer(reply)}
                          >
                            {t("communication.messages.detail.sendAnswer")}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => setAnswering(null)}
                          >
                            {t("communication.bulk.cancel")}
                          </Button>
                        </span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Drawer>
  )
}

export default MessageDetailDrawer
